import { useChat } from '@ai-sdk/react'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useCanGoBack, useNavigate, useRouter } from '@tanstack/react-router'
import type {
  CollectEmailToolOutput,
  CollectPhoneToolOutput,
  LeadView,
  SendLinkToolOutput,
  SlotsToolOutput,
} from '@tutlo/contracts'
import { cx } from '@tutlo/ui'
import { DefaultChatTransport, type UIMessage } from 'ai'
import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { trackBooked } from '../analytics.ts'
import { Shell } from '../layout.tsx'
import { useLead } from '../lead.ts'
import { AgentAvatar } from '../screens/chat/agent-avatar.tsx'
import { AnimatedBubbles, AnimatedText, type BubbleItem } from '../screens/chat/animated-text.tsx'
import { answeredOnLoad, shouldAutoSend } from '../screens/chat/auto-send.ts'
import { bookingCallIds } from '../screens/chat/booked.ts'
import { EmailPart, PhonePart } from '../screens/chat/contact-part.tsx'
import {
  type ContactState,
  contactSubmission,
  contactWidget,
} from '../screens/chat/contact-visibility.ts'
import { followToEnd, scrollToEnd, trackAtBottom } from '../screens/chat/follow-scroll.ts'
import { DaysPart, SlotMatchPart, SlotsPart } from '../screens/chat/slots-part.tsx'
import { TypingDots } from '../screens/chat/typing-dots.tsx'
import { formatTime, relativeDay } from '../time.ts'

export const Route = createFileRoute('/chat')({ component: ChatRoute })

/** AI SDK tool part in a UIMessage: `type: 'tool-<name>'` plus state and output. */
type ToolPart = {
  type: `tool-${string}`
  toolCallId: string
  state:
    | 'input-streaming'
    | 'input-available'
    | 'output-available'
    | 'output-error'
    | 'approval-requested'
    | 'approval-responded'
    | 'output-denied'
  output?: unknown
}

const isToolPart = (part: { type: string }): part is ToolPart => part.type.startsWith('tool-')

/**
 * Bubbles render plain text, so markdown markers the model sneaks in (`**termin**`) would show
 * literally. The prompt forbids markdown, but weaker models slip — strip inline markers here.
 * Single `*` stays untouched (can be a legit character).
 */
const stripInlineMarkdown = (text: string): string =>
  text
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/`([^`]+)`/g, '$1')

/** Tools with their own UI in the thread; the rest (book, update_lead, …) render nothing. */
const VISIBLE_TOOLS = new Set([
  'tool-get_slots',
  'tool-collect_email',
  'tool-collect_phone',
  'tool-send_link',
  'tool-handoff',
])

/**
 * Agent chat — the funnel's escape hatch. History is loaded via GET /api/chat before mounting
 * useChat; each turn streams through POST in the useChat protocol.
 */
function ChatRoute() {
  const navigate = useNavigate()
  const lead = useLead()
  const history = useQuery({
    queryKey: ['chat-history'],
    queryFn: async () => {
      const res = await fetch('/api/chat')
      if (!res.ok) throw new Error(`GET /api/chat ${res.status}`)
      return (await res.json()) as { messages: UIMessage[] }
    },
    staleTime: Number.POSITIVE_INFINITY,
  })

  if (lead.isPending || history.isPending) return <Shell nav={{}}>{null}</Shell>
  if (!lead.data) {
    navigate({ to: '/', replace: true })
    return null
  }
  return <ChatScreen lead={lead.data} initialMessages={history.data?.messages ?? []} />
}

function ChatScreen({ lead, initialMessages }: { lead: LeadView; initialMessages: UIMessage[] }) {
  const router = useRouter()
  const canGoBack = useCanGoBack()
  // Back = where the chat was opened from (waiting room, Hook); a fresh tab lands on the
  // waiting room once the lead has an e-mail, on the Hook before that.
  const leave = () => {
    if (canGoBack) return router.history.back()
    if (lead.email) return router.navigate({ to: '/s/$token', params: { token: lead.startToken } })
    return router.navigate({ to: '/' })
  }
  const [input, setInput] = useState('')
  // Forms answered before this mount must not fire a turn; anything filled from now on must
  // (auto-send.ts). The SDK's own `lastAssistantMessageIsCompleteWithToolCalls` is unusable here:
  // it also fires on a turn that merely ends with a completed SERVER tool, which loops forever.
  const autoSent = useRef(answeredOnLoad(initialMessages))
  const { messages, sendMessage, addToolOutput, status, error } = useChat({
    messages: initialMessages,
    transport: new DefaultChatTransport({ api: '/api/chat' }),
    sendAutomaticallyWhen: ({ messages }) => shouldAutoSend(messages, autoSent.current),
  })
  const busy = status === 'submitted' || status === 'streaming'
  const endRef = useRef<HTMLDivElement>(null)

  // The agent books on the server, so the ad conversion fires when its result reaches the thread.
  // Bookings already in the loaded history were counted in the session that made them.
  const trackedBookings = useRef(new Set(bookingCallIds(initialMessages)))
  useEffect(() => {
    for (const id of bookingCallIds(messages)) {
      if (trackedBookings.current.has(id)) continue
      trackedBookings.current.add(id)
      trackBooked(lead)
    }
  }, [messages, lead])

  // A contact form the user ignored used to vanish from the thread (the model was supposed to
  // re-open it and Haiku regularly does not), leaving the lead with „podaj e-mail w formularzu
  // powyżej" and no form. The newest request stays live until the data is actually captured.
  const contact: ContactState = { hasEmail: lead.email !== null, hasPhone: lead.phone !== null }
  for (const message of messages) {
    for (const part of message.parts) {
      if (!isToolPart(part)) continue
      if (part.type === 'tool-collect_email') contact.emailCallId = part.toolCallId
      if (part.type === 'tool-collect_phone') contact.phoneCallId = part.toolCallId
    }
  }
  const openForm =
    (!contact.hasEmail && contact.emailCallId !== undefined) ||
    (!contact.hasPhone && contact.phoneCallId !== undefined)

  // Keep the newest content in view while messages arrive and stream, unless the reader scrolled
  // up — they keep their place and get a „new message" button instead (follow-scroll.ts). Scroll
  // the window to its full height (after paint) instead of scrollIntoView — the sticky composer
  // would otherwise cover the last bubble.
  const [unseen, setUnseen] = useState(false)
  const prevStatus = useRef(status)
  const [away, setAway] = useState(false)
  useEffect(
    () =>
      trackAtBottom((atBottom) => {
        setAway(!atBottom)
        if (atBottom) setUnseen(false)
      }),
    [],
  )
  const lastMessage = messages.at(-1)
  const streamAnchor = lastMessage ? JSON.stringify(lastMessage.parts).length : 0
  // Typing dots stay up until the reply's first words render — a tool call before the text
  // (get_slots) would otherwise leave a silent gap with no avatar.
  const lastHasVisibleText =
    lastMessage?.role === 'assistant' &&
    lastMessage.parts.some((part) => part.type === 'text' && part.text.trim() !== '')
  useEffect(() => {
    if (streamAnchor === 0 && status !== 'submitted') return
    // The reader's own message (typed, quick reply, form) always brings them down to the reply —
    // once, on sending; chunks that arrive while still `submitted` must not yank them back.
    const justSent = status === 'submitted' && prevStatus.current !== 'submitted'
    prevStatus.current = status
    const frame = requestAnimationFrame(() => {
      if (justSent) scrollToEnd()
      else if (!followToEnd()) setUnseen(true)
    })
    return () => cancelAnimationFrame(frame)
  }, [streamAnchor, status])

  // Standing suggestions render only under a "quiet" agent message — one that shows no
  // widget of its own (slot picker, contact form) and didn't hand off.
  const lastParts = lastMessage?.role === 'assistant' ? lastMessage.parts : []
  const lastInteractive = lastParts.some((part) => {
    if (!isToolPart(part)) return false
    if (part.type === 'tool-get_slots')
      // A `match` result renders a static confirmation — nothing for the user to click.
      return part.state !== 'output-available' || (part.output as SlotsToolOutput).kind !== 'match'
    return part.type === 'tool-handoff'
  })
  const booked =
    lead.activeBookingId !== null ||
    messages.some((message) =>
      message.parts.some(
        (part) =>
          isToolPart(part) &&
          part.type === 'tool-book' &&
          part.state === 'output-available' &&
          (part.output as { ok?: boolean }).ok === true,
      ),
    )
  // A message that ends with a question awaits the user's answer — suggestions under it would
  // read as answer options. Simple text heuristic, no model involvement needed.
  const lastText = lastParts.filter((part) => part.type === 'text').at(-1)
  const endsWithQuestion = /\?\s*$/.test(lastText?.text.trim() ?? '')
  const quickReplies =
    status === 'ready' &&
    lastMessage?.role === 'assistant' &&
    !lastInteractive &&
    !openForm &&
    !endsWithQuestion
      ? booked
        ? QUICK_REPLIES_BOOKED
        : QUICK_REPLIES
      : []

  // The composer grows with its text up to 3 lines (max-h = 3 × 24 px + padding), then scrolls
  // inside — more would crowd the thread on short phones (Meta's in-app browser on 320 px phones
  // is ~480 px tall) once the keyboard is up.
  const composerRef = useRef<HTMLTextAreaElement>(null)
  // biome-ignore lint/correctness/useExhaustiveDependencies: `input` is the trigger — re-measure on every change
  useLayoutEffect(() => {
    const el = composerRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [input])

  const send = (text: string) => {
    const trimmed = text.trim()
    if (!trimmed || busy) return
    void sendMessage({ text: trimmed })
    setInput('')
  }

  const pickSlot = (slot: { start: string }) => {
    send(`Wybieram termin: ${relativeDay(slot.start).toLowerCase()} ${formatTime(slot.start)}`)
  }

  return (
    <Shell
      nav={{ back: leave }}
      desktopFill
      stickyHeader
      stickyCta
      title={<ChatHeader />}
      cta={
        <div className="relative">
          {/* Anyone reading above the end gets a way back down; a reply they have not seen yet
              adds the label to the same pill. */}
          {away || unseen ? (
            <div className="-top-[52px] pointer-events-none absolute inset-x-0 flex justify-center">
              <button
                type="button"
                onClick={scrollToEnd}
                aria-label={unseen ? undefined : 'Przewiń na dół'}
                className="chat-msg-in pointer-events-auto inline-flex cursor-pointer items-center gap-[6px] rounded-pill border border-border-soft bg-bg-card px-base text-label-strong text-selected shadow-card active:scale-95 h-[38px]"
              >
                <svg viewBox="0 0 24 24" fill="none" className="size-4" aria-hidden="true">
                  <path
                    d="M12 5v14m0 0 6-6m-6 6-6-6"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {unseen ? 'Nowa wiadomość' : null}
              </button>
            </div>
          ) : null}
          <form
            onSubmit={(e) => {
              e.preventDefault()
              send(input)
            }}
            // Radius = half the one-line height, so it is a pill on one line and a rounded box as it
            // grows; items-end keeps the send button by the last line.
            className="flex items-end gap-sm rounded-[24px] border border-border-soft bg-bg-card py-[3px] pr-[3px] pl-base shadow-soft focus-within:border-selected-line"
          >
            <textarea
              ref={composerRef}
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                // Enter sends (the keyboard's key says „send"), Shift+Enter breaks the line; never
                // mid-composition (IME, dictation).
                if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return
                e.preventDefault()
                send(input)
              }}
              placeholder="Napisz wiadomość…"
              aria-label="Twoja wiadomość"
              enterKeyHint="send"
              className="max-h-[88px] min-w-0 flex-1 resize-none bg-transparent py-[8px] text-body text-text-primary leading-[1.5] outline-none placeholder:text-text-secondary"
            />
            <button
              type="submit"
              disabled={busy || input.trim() === ''}
              aria-label="Wyślij"
              className="flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-pill bg-action text-action-text shadow-cta transition-[transform,filter] duration-150 ease-out-soft hover:brightness-[.98] active:scale-95 disabled:pointer-events-none disabled:opacity-40"
            >
              <svg viewBox="0 0 24 24" fill="none" className="size-5" aria-hidden="true">
                <path
                  d="M12 19V5m0 0-6 6m6-6 6 6"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </form>
        </div>
      }
    >
      <div className="flex flex-1 flex-col gap-base pt-base pb-base">
        {messages.length === 0 ? (
          <Welcome
            firstName={lead.firstName}
            hasBooking={lead.activeBookingId !== null}
            onPick={send}
            disabled={busy}
          />
        ) : null}
        {messages.map((message, i) => (
          <ChatMessage
            key={message.id}
            message={message}
            streaming={status === 'streaming' && i === messages.length - 1}
            isLast={i === messages.length - 1}
            busy={busy}
            contact={contact}
            onPickSlot={pickSlot}
            onQuickReply={send}
            onContactDone={(tool, toolCallId, output, live) => {
              const next = contactSubmission(tool, live, output)
              if (next.kind === 'tool-output') addToolOutput({ tool, toolCallId, output })
              else void sendMessage({ text: next.text })
            }}
          />
        ))}
        {quickReplies.length > 0 ? (
          <div className="flex flex-wrap gap-sm pl-[32px]">
            {quickReplies.map((text, i) => (
              <button
                key={text}
                type="button"
                disabled={busy}
                onClick={() => send(text)}
                className={cx('chat-msg-in', CHIP_CLASS)}
                style={{ animationDelay: `${400 + i * 120}ms` }}
              >
                {text}
              </button>
            ))}
          </div>
        ) : null}
        {status === 'submitted' || (status === 'streaming' && !lastHasVisibleText) ? (
          <div className="chat-msg-in flex items-end gap-sm">
            <AgentAvatar />
            <div className="chat-bubble-agent rounded-card-lg rounded-bl-card-sm bg-bg px-md py-sm">
              <TypingDots />
            </div>
          </div>
        ) : null}
        {error ? (
          <p
            className="chat-msg-in rounded-card-lg bg-warning-soft px-base py-md text-label text-warning-text"
            role="alert"
          >
            Coś poszło nie tak albo limit wiadomości na tę godzinę się wyczerpał. Spróbuj za chwilę.
          </p>
        ) : null}
        <div ref={endRef} />
      </div>
    </Shell>
  )
}

function ChatHeader() {
  return (
    <div className="flex min-w-0 items-center gap-sm">
      <AgentAvatar className="size-8 shadow-selected" />
      <div className="flex flex-col">
        <p className="text-body-strong text-text-primary leading-tight">Asystent AI Tutlo</p>
        <p className="flex items-center gap-[6px] whitespace-nowrap text-label text-text-secondary leading-tight">
          <span className="size-[7px] shrink-0 rounded-pill bg-success" aria-hidden="true" />
          {/* The tail would wrap to a second line on 320 px phones — „Online" alone says enough. */}
          <span>
            Online<span className="max-[359px]:hidden"> — odpowiada od razu</span>
          </span>
        </p>
      </div>
    </div>
  )
}

const CHIP_CLASS =
  'cursor-pointer rounded-pill border border-selected-line bg-bg-card px-base py-sm text-label-strong text-selected transition-[background-color,transform] duration-150 ease-out-soft hover:bg-selected-soft active:scale-95 disabled:opacity-50'

const STARTERS = [
  'Umów mnie na lekcję próbną',
  'Jak wygląda lekcja próbna?',
  'Na czym polega nauka w Tutlo?',
]
// Short enough for one line at 320 px.
const STARTERS_BOOKED = [
  'Jak dołączę do lekcji?',
  'Jak się przygotować?',
  'Dopasujcie lekcję do mnie',
]

/** Persistent quick replies under agent messages that carry no interactive widget of their own. */
const QUICK_REPLIES = [
  'Umów mnie na lekcję próbną',
  'Czym jest Tutlo?',
  'Jak wygląda lekcja próbna?',
]
const QUICK_REPLIES_BOOKED = ['Jak dołączę do lekcji?', 'Jak się przygotować?']

/**
 * Scripted opener rendered as an agent bubble (no model call, no tokens) with the same word
 * reveal as live streaming, then staggered quick-reply chips.
 */
function Welcome({
  firstName,
  hasBooking,
  onPick,
  disabled,
}: {
  firstName: string | null
  hasBooking: boolean
  onPick: (text: string) => void
  disabled: boolean
}) {
  const hello = `Cześć${firstName ? ` ${firstName}` : ''}! 👋`
  const greeting = hasBooking
    ? `${hello} Tu wirtualny asystent Tutlo (AI). Twoja lekcja próbna jest umówiona. Pomogę Ci się do niej przygotować, odpowiem na pytania albo zmienię termin — co wolisz?`
    : `${hello} Jestem wirtualnym asystentem Tutlo (AI). Umówię Cię na bezpłatną lekcję próbną i odpowiem na pytania o naukę angielskiego. W czym mogę pomóc?`
  const starters = hasBooking ? STARTERS_BOOKED : STARTERS
  return (
    <div className="flex flex-col gap-md">
      <div className="flex items-end gap-sm">
        <AgentAvatar />
        <p className="max-w-[85%] rounded-card-lg rounded-bl-card-sm bg-bg px-base py-md text-body leading-[1.5] text-text-primary">
          <AnimatedText text={greeting} animate />
        </p>
      </div>
      <div className="flex flex-wrap gap-sm pl-[32px]">
        {starters.map((text, i) => (
          <button
            key={text}
            type="button"
            disabled={disabled}
            onClick={() => onPick(text)}
            className={cx('chat-msg-in', CHIP_CLASS)}
            style={{ animationDelay: `${1500 + i * 150}ms` }}
          >
            {text}
          </button>
        ))}
      </div>
    </div>
  )
}

function ChatMessage({
  message,
  streaming,
  isLast,
  busy,
  contact,
  onPickSlot,
  onQuickReply,
  onContactDone,
}: {
  message: UIMessage
  streaming: boolean
  isLast: boolean
  busy: boolean
  contact: ContactState
  onPickSlot: (slot: { start: string }) => void
  onQuickReply: (text: string) => void
  onContactDone: (
    tool: 'collect_email' | 'collect_phone',
    toolCallId: string,
    output: CollectEmailToolOutput | CollectPhoneToolOutput,
    /** The tool call still belongs to the last message, so the SDK can take its output. */
    live: boolean,
  ) => void
}) {
  const mine = message.role === 'user'
  // Keys: tool parts have `toolCallId`; text parts get an ordinal (stable once the stream finishes).
  // Items keep the thread's chronology (a saved-contact pill stays where the form was — also while
  // the same message keeps streaming), with one exception: slot grids hop AFTER the next text
  // bubble, because the model calls get_slots before phrasing the sentence that introduces the
  // terms; until that sentence exists they stay hidden.
  const items: BubbleItem[] = []
  // Presenter widgets waiting for the sentence that introduces them.
  let pending: BubbleItem[] = []
  let textNo = 0
  for (const part of message.parts) {
    if (part.type === 'text') {
      textNo += 1
      // Blank lines split one text part into separate bubbles — nobody sends a wall of
      // text with paragraphs in a messenger. Segments are append-only while streaming.
      const pieces = stripInlineMarkdown(part.text)
        .split(/\n{2,}/)
        .filter((piece) => piece.trim() !== '')
      for (const [segNo, piece] of pieces.entries()) {
        items.push({ kind: 'text', key: `${message.id}-text-${textNo}-${segNo}`, text: piece })
      }
      if (pieces.length > 0) {
        items.push(...pending)
        pending = []
      }
      continue
    }
    if (!isToolPart(part) || !VISIBLE_TOOLS.has(part.type)) continue
    const item: BubbleItem = {
      kind: 'ui',
      key: part.toolCallId,
      node: (
        <div key={part.toolCallId} className="w-full max-w-[95%]">
          <ChatToolPart
            part={part}
            busy={busy}
            isLast={isLast}
            contact={contact}
            onPickSlot={onPickSlot}
            onQuickReply={onQuickReply}
            onContactDone={onContactDone}
          />
        </div>
      ),
    }
    if (part.type === 'tool-get_slots') pending.push(item)
    else items.push(item)
  }
  if (!streaming) items.push(...pending)
  if (items.length === 0) return null
  const bubbleClass = cx(
    'chat-msg-in max-w-[85%] whitespace-pre-wrap px-base py-md text-body leading-[1.5]',
    mine
      ? 'chat-bubble-mine rounded-card-lg rounded-br-card-sm bg-linear-to-br from-selected-light to-selected text-white'
      : 'chat-bubble-agent rounded-card-lg rounded-bl-card-sm bg-bg text-text-primary',
  )
  return (
    <div className={cx('flex gap-sm', mine ? 'justify-end' : 'items-end')}>
      {mine ? null : <AgentAvatar />}
      <div
        className={cx('flex min-w-0 flex-1 flex-col gap-sm', mine ? 'items-end' : 'items-start')}
      >
        {mine ? (
          items.map((item) =>
            item.kind === 'text' ? (
              <p key={item.key} className={bubbleClass}>
                {item.text}
              </p>
            ) : (
              item.node
            ),
          )
        ) : (
          <AnimatedBubbles items={items} animate={streaming} bubbleClass={bubbleClass} />
        )}
      </div>
    </div>
  )
}

function ChatToolPart({
  part,
  busy,
  isLast,
  contact,
  onPickSlot,
  onQuickReply,
  onContactDone,
}: {
  part: ToolPart
  busy: boolean
  isLast: boolean
  contact: ContactState
  onPickSlot: (slot: { start: string }) => void
  onQuickReply: (text: string) => void
  onContactDone: (
    tool: 'collect_email' | 'collect_phone',
    toolCallId: string,
    output: CollectEmailToolOutput | CollectPhoneToolOutput,
    /** The tool call still belongs to the last message, so the SDK can take its output. */
    live: boolean,
  ) => void
}) {
  switch (part.type) {
    case 'tool-get_slots': {
      if (part.state !== 'output-available') {
        return (
          <p className="chat-msg-in text-label text-text-secondary">Sprawdzam wolne terminy…</p>
        )
      }
      const output = part.output as SlotsToolOutput
      if (output.kind === 'days') {
        // Day chips behave like question chips: gone once any answer lands.
        if (!isLast) return null
        return <DaysPart days={output.days} onPick={onQuickReply} disabled={busy} />
      }
      if (output.kind === 'match') {
        // The user already named this hour — confirm it, don't re-ask with a picker.
        return <SlotMatchPart slot={output.slot} durationMin={output.durationMin} />
      }
      return (
        <SlotsPart
          slots={output.slots}
          durationMin={output.durationMin}
          onPick={onPickSlot}
          disabled={busy || !isLast}
        />
      )
    }
    // Contact steps render by captured data, not by position in the thread (contact-visibility.ts).
    case 'tool-collect_email': {
      const shown = contactWidget(part, contact)
      if (shown === 'hidden') return null
      if (shown === 'saved') return <SavedPill>E-mail zapisany</SavedPill>
      const live = isLast && part.state === 'input-available'
      return (
        <EmailPart
          onDone={(output) => onContactDone('collect_email', part.toolCallId, output, live)}
        />
      )
    }
    case 'tool-collect_phone': {
      const shown = contactWidget(part, contact)
      if (shown === 'hidden') return null
      if (shown === 'saved') return <SavedPill>Numer zapisany</SavedPill>
      const live = isLast && part.state === 'input-available'
      return (
        <PhonePart
          onDone={(output) => onContactDone('collect_phone', part.toolCallId, output, live)}
        />
      )
    }
    case 'tool-send_link': {
      if (part.state !== 'output-available') return null
      const output = part.output as SendLinkToolOutput
      return (
        <a
          href={output.url}
          className="chat-msg-in inline-flex cursor-pointer items-center gap-sm rounded-pill border border-selected-line bg-bg-card px-lg py-md text-button text-selected transition-[background-color,transform] duration-150 ease-out-soft hover:bg-selected-soft active:scale-95"
        >
          Otwórz swoją poczekalnię →
        </a>
      )
    }
    case 'tool-handoff':
      // Status badge only — the model's own farewell sentence carries the prose. Wording stays at
      // "write to us": nothing reads the handoff event yet, so promising a queue would be a lie.
      if (part.state !== 'output-available') return null
      return (
        <p className="chat-msg-in inline-flex items-center gap-sm rounded-pill bg-selected-soft px-base py-sm text-label-strong text-selected">
          <span aria-hidden="true">✉️</span>
          Napisz do nas: kontakt@tutlo.pl
        </p>
      )
    default:
      return null
  }
}

function SavedPill({ children }: { children: ReactNode }) {
  return (
    <p className="chat-msg-in flex items-center gap-sm rounded-card-lg bg-success-soft px-base py-md text-label-strong text-success-text">
      <span
        aria-hidden="true"
        className="flex size-[22px] items-center justify-center rounded-pill bg-success text-white"
      >
        ✓
      </span>
      {children}
    </p>
  )
}
